import { useIsCrossCategoryLinkEnabled } from '@client/hooks/use-cross-category-link';
import { useIsPriceEstimatorEnabled } from '@client/hooks/use-pricing';
import { useIsReviewSnippetsOnVendorStorefrontsEnabled } from '@client/hooks/use-review-snippets-on-vendor-storefronts';
import { useIsWeddingsHostedCountBadgeEnabled } from '@client/hooks/use-weddings-hosted-count-badge';
import { useIsMarkAsBookedLiteStorefrontsEnabled } from '@client/hooks/useMarkAsBookedLiteStorefronts';
import { isReviewFlowExitModalV2Selector } from '@client/redux/experiments/selectors/review-flow-exit-modal-v2';
import { isInReviewsLayoutAlignmentExperiment } from '@client/redux/experiments/selectors/reviews-layout-alignment';
import { AboutSummary } from '@components/about-summary/about-summary';
import { PackagePricing } from '@components/detailed-pricing/PackagePricing';
import { FooterCategoriesV2 } from '@components/footer_categories/footer-categories-v2';
import { ListingsSearchBarConnected } from '@components/listings-search-bar';
import { pageLocationHashAtom } from '@components/listings-search-bar/atoms';
import { VenuePricing } from '@components/pricing/VenuePricing';
import { reviewsWithPhotosCountAtom } from '@components/reviews/atoms';
import { getBadges } from '@components/vendorCard/public/extractVendorProps/extract/badges';
import { getDeals } from '@components/vendorCard/public/extractVendorProps/extract/deals';
import { VENDOR_REFERRED_MARKET_CODE } from '@constants/cookies';
import { useStorefrontDisplayPriceRange } from '@feature-flags/hooks/Storefront/useStorefrontDisplayPriceRange';
import { useStorefrontEmptyStates } from '@feature-flags/hooks/Storefront/useStorefrontEmptyStates';
import { useUserEngagementInteractivity } from '@feature-flags/hooks/Storefront/useUserEngagementInteractivity';
import { useDetailedPricing } from '@hooks/use-detailed-pricing';
import { useIsVenue } from '@hooks/use-is-venue';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useStorefrontCollapsedRfq } from '@hooks/use-storefront-collapsed-rfq';
import { useStorefrontPeaceOfMind } from '@hooks/use-storefront-peace-of-mind';
import { useIsStorefrontsNetworkEnabled } from '@hooks/use-storefronts-network';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useStorefrontCouplesUsuallySpend } from '@hooks/useStorefrontCouplesUsuallySpend';
import { isInReviewPhotoFilterExperiment } from '@redux/experiments/selectors/review-photo-filter';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setVendorReferredMarketCode } from '@redux/settings';
import { Link } from '@tkww/orion-web-ds-action';
import { Spinner } from '@tkww/orion-web-ds-feedback';
import { PageContainer } from '@tkww/orion-web-ds-page-layout';
import { GridColumn, GridRow } from '@tkww/orion-web-ds-structure';
import classNames from 'classnames';
import { CookieStorage } from 'cookie-storage';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useMemo } from 'react';
import { StickyContainer } from 'react-sticky';
import type { Decorated, Raw } from 'types/vendor';
import AnalyticsConstants from '../../../constants/analytics';
import { composeStorefrontCanonicalUrl } from '../../../helpers/seo';
import VendorHelper from '../../../helpers/vendor';
import ExitEarlyAlert from '../../components/auto-account-creation/exitEarlyAlert/ExitEarlyAlert';
import Breadcrumbs from '../../components/shared/Breadcrumbs';
import MetaContent from '../../components/shared/HelmetHeader/HelmetHeader';
import { Widget } from '../../containers/widget';
import { RecommendedContextProvider } from '../../contexts/RecommendedContext/RecommendedContext';
import {
	metaDescriptionAtom,
	metaTitleAtom,
} from '../../jotai/shared/seo-experiment';
import {
	ContentGatingStatus,
	availabilityContentGateAtom,
	venuePricingContentGateAtom,
} from '../../jotai/storefront/content-gating';
import { hideDetailedPricingAtom } from '../../jotai/storefront/detailed-pricing';
import { teamMembersAtom } from '../../jotai/storefront/organization';
import { hidePricingAtom } from '../../jotai/storefront/pricing';
import Styles from './Storefront.scss';
import AvailabilitySection from './components/AvailabilitySection/AvailabilitySection';
import { availabilityDataAtom } from './components/AvailabilitySection/availability-atoms';
import { LiteWidget } from './components/LiteWidget';
import { McVrmLiteWidget } from './components/McVrmLiteWidget/McVrmLiteWidget';
import { RfqDeeplink } from './components/RfqDeeplink/RfqDeeplink';
import { VendorNetworkBanner } from './components/VendorNetworkBanner';
import WhyUseKnotFullWidth from './components/WhyUseKnotFullWidth';
import CtaWrapper from './containers/CtaWrapper/CtaWrapper';
import StorefrontGallery from './containers/StorefrontGallery/StorefrontGallery';
import { ContactLocation } from './containers/contact';
import { Deals } from './containers/deals';
import { Details } from './containers/details/Details';
import DetailsHelper from './containers/details/helpers';
import { LiteVendorAbout } from './containers/lite_vendor_about';
import { MeetTheTeam } from './containers/meet_the_team/meet_the_team';
import { Nav } from './containers/nav';
import { Reviews } from './containers/reviews/components/reviews/Reviews';
import TourLightbox from './containers/tour_lightbox';
import { VendorHeader } from './containers/vendor_header/VendorHeader';
import { useIsVendorBooked } from './containers/vendor_info/components/BookVendor/hooks/use-is-vendor-booked';
import { buildProperties } from './helpers';
import { useFetchBookings } from './hooks/use-fetch-bookings';
import { useGetConversions } from './hooks/use-get-conversations';
import { useOrderRecentlySeenVendors } from './hooks/use-order-recently-seen-vendors';
import { useStorefrontExperiments } from './hooks/use-storefront-experiments';

const cookieStorage = new CookieStorage();

const Storefront = () => {
	const dispatch = useAppDispatch();
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const vendorRaw = useAppSelector((state) => state.vendor.vendorRaw as Raw);
	const viewport = useAppSelector((state) => state.viewport);
	const category = useAppSelector((state) => state.category);
	const memberId = useAppSelector((state) => state.membership.member?.id || '');
	const member = useAppSelector((state) => state.membership.member);
	const searchPageLocation = useAppSelector(
		(state) => state.searchPageLocation,
	);
	const setPageLocationHash = useSetAtom(pageLocationHashAtom);
	const crossCatLength = useAppSelector(
		(state) =>
			state.widget.pvRecCrossCatVendorsReducer.recommendedVendors.length,
	);
	const userEngagementInteractivityAssignmentVariant =
		useUserEngagementInteractivity();
	const isVenue = useIsVenue();
	const ogImage = vendor.logo?.url;
	const pathname = vendor.uriData?.uri;
	const { state } = vendor.address;
	const { isMobileApp } = viewport;
	const isLiteStorefront = useLiteStorefront();
	const isDetailedPricing = useDetailedPricing();
	const isEmptyStates = useStorefrontEmptyStates();
	const availabilityData = useAtomValue(availabilityDataAtom);
	const isCusEnabled = useStorefrontCouplesUsuallySpend();
	const deals = getDeals(vendorRaw);
	const shouldShowDeals = vendor?.isPaid === true && deals.length > 0;
	const shouldHidePricing = useAtomValue(hidePricingAtom);
	const shouldHideDetailedPricing = useAtomValue(hideDetailedPricingAtom);
	useStorefrontExperiments();
	const metaTitleFromExperiment = useAtomValue(metaTitleAtom);
	const metaDescriptionFromExperiment = useAtomValue(metaDescriptionAtom);
	const venuePricingContentGate = useAtomValue(venuePricingContentGateAtom);
	const isVenuePricingContentBlocked =
		venuePricingContentGate === ContentGatingStatus.BLOCKED;
	const availabilityContentGate = useAtomValue(availabilityContentGateAtom);
	const shouldShowAvailability =
		vendor.isPaid && availabilityContentGate !== ContentGatingStatus.BLOCKED;
	const badgeCounts = getBadges(vendorRaw);
	const isMarkAsBookedLiteStorefrontsEnabled =
		useIsMarkAsBookedLiteStorefrontsEnabled();
	const markVendorAsBookedEnabled =
		vendor.isPaid || isMarkAsBookedLiteStorefrontsEnabled;
	const isCrossCategoryLinkEnabled = useIsCrossCategoryLinkEnabled();
	const isStorefrontsNetworkEnabled = useIsStorefrontsNetworkEnabled();

	const reviewFlowExitModalV2Enabled = useAppSelector((state) =>
		isReviewFlowExitModalV2Selector(state),
	);

	const isPriceEstimatorEnabled = useIsPriceEstimatorEnabled();
	const isStorefrontPeaceOfMind = useStorefrontPeaceOfMind();

	const bookings = useAppSelector((state) => state.bookings.bookings);
	const isVendorBooked = useIsVendorBooked(bookings, vendor.id);

	const showReviewsDivider =
		isLiteStorefront || (isStorefrontsNetworkEnabled && !isVendorBooked);

	const reviewsWithPhotosCount = useAtomValue(reviewsWithPhotosCountAtom);

	const isInReviewPhotoFilterExperimentValue = useAppSelector(
		isInReviewPhotoFilterExperiment,
	);

	const properties = useMemo(() => {
		const badgeNames = badgeCounts.map((badge) => badge.label);
		const shouldReportPhotosCount =
			isInReviewPhotoFilterExperimentValue && reviewsWithPhotosCount > 0;

		const baseProperties = buildProperties({
			vendor,
			viewport,
			memberId,
			badges: badgeNames,
		});

		return {
			...baseProperties,
			...(shouldShowDeals && { vendor_deals_count: deals.length }),
			...(shouldReportPhotosCount && {
				vendor_reviews_with_photos_count: reviewsWithPhotosCount,
			}),
		};
	}, [
		vendor,
		viewport,
		memberId,
		shouldShowDeals,
		deals.length,
		badgeCounts,
		isInReviewPhotoFilterExperimentValue,
		reviewsWithPhotosCount,
	]);
	const defaultMetaTitle = `${vendor.name} + Updated Prices`;
	const metaTitle =
		metaTitleFromExperiment === null
			? defaultMetaTitle
			: metaTitleFromExperiment;
	const metaDescription =
		metaDescriptionFromExperiment === null
			? VendorHelper.metaDescription(vendor, category)
			: metaDescriptionFromExperiment;
	const hasTeamMembers = useAtomValue(teamMembersAtom).length > 0;
	const awards = useMemo(() => {
		const affiliates = vendorRaw.affiliates;
		if (affiliates.length) {
			return affiliates[0].affiliates.reduce<string[]>((acc, curr) => {
				if (curr.name) {
					acc.push(curr.name);
				}
				return acc;
			}, []);
		}

		return [];
	}, [vendorRaw.affiliates]);

	useGetConversions(cookieStorage);
	useOrderRecentlySeenVendors();

	// Track storefront viewed
	const { track } = useAnalyticsContext();
	// biome-ignore lint/correctness/useExhaustiveDependencies: onMount only
	useEffect(() => {
		track({
			event: AnalyticsConstants.STOREFRONT_VIEWED_VTR,
			properties,
		});
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: onMount only
	useEffect(() => {
		cookieStorage.removeItem(VENDOR_REFERRED_MARKET_CODE);
		dispatch(setVendorReferredMarketCode(properties.vendorReferredMarketCode));
	}, []);

	// Lock the search bar location to the vendor's actual address.
	// Note: the in-bar location typeahead/picker was removed as part of the
	// new search-bar changes; pages that need to force a location should set
	// `pageLocationHashAtom` (done here). We intentionally use `vendor.address`
	// rather than `searchPageLocation` because `searchPageLocation` reflects the
	// referring search page (e.g. Wichita) while the vendor's address is the
	// vendor's actual location (e.g. Sedgwick). `searchPageLocation` continues
	// to drive Breadcrumbs and `FooterCategoriesV2` unchanged.
	useEffect(() => {
		const city = vendor.address.city;
		const stateCode = vendor.address.state;
		// Only lock when we have a valid location (city + stateCode are required
		// for URL path building and API calls). If either is missing we leave
		// `pageLocationHashAtom` as null so `useResolvedLocation()` will fall back
		// to Redux `state.location` (IP-based geolocation) as a safe default.
		if (!city || !stateCode) return;
		setPageLocationHash({
			city,
			stateCode,
			market: vendor.marketCode ?? null,
		});
		return () => {
			setPageLocationHash(null);
		};
	}, [vendor.address, vendor.marketCode, setPageLocationHash]);

	useFetchBookings(category.code, member);

	const priceRangeDisplay = useStorefrontDisplayPriceRange();
	const inReviewsLayoutAlignmentExperiment = useAppSelector(
		isInReviewsLayoutAlignmentExperiment,
	);

	const isCollapsedRfqEnabled = useStorefrontCollapsedRfq();
	const isReviewSnippetsVariant =
		useIsReviewSnippetsOnVendorStorefrontsEnabled();
	const isWeddingsHostedCountBadgeEnabled =
		useIsWeddingsHostedCountBadgeEnabled();

	return (
		<RecommendedContextProvider>
			<PageContainer
				id="storefront-v4"
				data-testid="storefront-v4"
				data-categorycode={category.code}
				data-purchase-status={vendor.purchaseStatus}
				data-lite-storefront={isLiteStorefront}
				data-review-layout-alignment={inReviewsLayoutAlignmentExperiment}
				data-detailed-pricing={isDetailedPricing}
				data-empty-states={isEmptyStates}
				data-usually-spend={isCusEnabled}
				data-user-engagement-interactivity={
					userEngagementInteractivityAssignmentVariant
				}
				data-is-storefront-page={true}
				data-is-mark-vendor-as-booked-enabled={markVendorAsBookedEnabled}
				data-is-storefronts-network-enabled={isStorefrontsNetworkEnabled}
				data-is-vendor-network-enabled={isStorefrontsNetworkEnabled}
				data-is-review-flow-exit-modal-v2-enabled={reviewFlowExitModalV2Enabled}
				data-is-price-estimator-enabled={isPriceEstimatorEnabled}
				data-is-peace-of-mind-enabled={isStorefrontPeaceOfMind}
				data-is-collapsed-rfq-enabled={isCollapsedRfqEnabled}
				data-is-review-snippets-on-vendor-storefronts-enabled={
					isReviewSnippetsVariant
				}
				data-is-weddings-hosted-count-badge-enabled={
					isWeddingsHostedCountBadgeEnabled
				}
				data-is-cross-category-link-enabled={isCrossCategoryLinkEnabled}
			>
				{!isMobileApp && (
					<GridRow>
						<GridColumn xs="12">
							<div className={Styles.storefrontContainer}>
								<ExitEarlyAlert isStorefrontPage />
								<div className={Styles.breadcrumbsSearchBarContainer}>
									<div className={Styles.breadcrumbsSearchBar}>
										<Breadcrumbs
											categoryCode={category.code}
											city={searchPageLocation.city}
											isHeader
											stateCode={searchPageLocation.stateCode}
											vendor={vendor}
										/>
									</div>
									<ListingsSearchBarConnected />
								</div>
							</div>
						</GridColumn>
					</GridRow>
				)}
				<GridRow>
					<GridColumn xs="12" lg={isLiteStorefront ? '8' : '12'}>
						<TourLightbox />
						<MetaContent
							canonical={composeStorefrontCanonicalUrl(
								vendor.uriData.relativeUri,
							)}
							description={metaDescription}
							landingPage="storefront"
							ogImage={ogImage}
							pathname={pathname}
							title={metaTitle}
						/>
						<div className={Styles.overlay}>
							<Spinner />
						</div>
						{!vendor.isPaid && !isLiteStorefront && (
							<GridRow hGutters={false}>
								<Widget
									className={Styles.topWidget}
									cardType="sm"
									algorithmType="SIMILAR"
									impressionType="Rec Module: Storefront - Top"
								/>
							</GridRow>
						)}
						<RfqDeeplink />
						<StorefrontGallery />
						<StickyContainer
							itemScope
							itemType="http://schema.org/LocalBusiness"
						>
							<GridRow>
								<GridColumn
									xs="12"
									lg={isLiteStorefront ? '12' : '8'}
									xl={isLiteStorefront ? '11' : '8'}
									className={classNames({
										[Styles.inlineCentering]: isLiteStorefront,
										[Styles.inlineCenteringFull]: !isLiteStorefront,
									})}
								>
									<Nav vendor={vendor}>
										<VendorHeader
											awards={awards}
											className={classNames(
												Styles.navContainerPaddingNoPadding,
												Styles.divider,
											)}
											vendor={vendor}
											priceRangeDisplay={priceRangeDisplay || undefined}
										/>
										{isStorefrontsNetworkEnabled && isVendorBooked && (
											<VendorNetworkBanner />
										)}
										{shouldShowDeals && <Deals title="Deals" deals={deals} />}

										{isLiteStorefront ? (
											<LiteVendorAbout
												className={classNames(
													Styles.navContainerPaddingNoPadding,
													{ [Styles.divider]: isLiteStorefront },
												)}
												title="About"
											/>
										) : (
											<AboutSummary
												title="About"
												className={Styles.noDivider}
											/>
										)}
										{isVenue && !isVenuePricingContentBlocked && (
											<VenuePricing
												title="Pricing"
												shouldHide={shouldHidePricing}
												shouldHideMenuItem={shouldHidePricing}
											/>
										)}
										{!isVenue && (
											<PackagePricing
												title="Pricing"
												shouldHide={shouldHideDetailedPricing}
												shouldHideMenuItem={shouldHideDetailedPricing}
											/>
										)}
										<Details
											shouldHide={!DetailsHelper.hasDetails(vendor)}
											title={
												vendor.categoryCode === 'REC' ? 'Amenities' : 'Details'
											}
											className={classNames({
												[Styles.divider]: isLiteStorefront,
											})}
										/>
										{vendor.isPaid && hasTeamMembers && (
											<MeetTheTeam title="Team" />
										)}
										{shouldShowAvailability && (
											<AvailabilitySection
												title="Availability"
												availabilityData={availabilityData}
											/>
										)}
										{!vendor.isPaid && <McVrmLiteWidget />}
										<Reviews
											title="Reviews"
											className={classNames({
												[Styles.divider]: showReviewsDivider,
												[Styles.noDivider]: !showReviewsDivider,
											})}
										/>
										{isStorefrontsNetworkEnabled && !isVendorBooked && (
											<VendorNetworkBanner />
										)}
										<ContactLocation
											title="Contact"
											className={classNames({
												[Styles.noDivider]: !isLiteStorefront,
											})}
										/>
									</Nav>
									{isLiteStorefront && (
										<LiteWidget
											className={Styles.liteWidgetMobile}
											algorithmType="BOW"
										/>
									)}
									{!isMobileApp && isLiteStorefront && (
										<FooterCategoriesV2
											city={searchPageLocation.city || ''}
											stateCode={state}
										/>
									)}
								</GridColumn>
								<GridColumn xs="12" lg="4">
									<CtaWrapper />
								</GridColumn>
							</GridRow>
						</StickyContainer>
						{!isMobileApp && !isLiteStorefront && (
							<FooterCategoriesV2
								city={searchPageLocation.city || ''}
								stateCode={state}
							/>
						)}
						{!isMobileApp && (
							<div className={Styles.footerCategoriesBreadcrumbContainer}>
								<Breadcrumbs
									categoryCode={category.code}
									city={searchPageLocation.city}
									stateCode={searchPageLocation.stateCode}
									vendor={vendor}
								/>
							</div>
						)}
						<Link
							href={`${vendor.uriData.relativeUri}/photos`}
							styleType="secondary"
							className={Styles.photosLink}
						>
							{vendor.name}'s photos
						</Link>
						{!isLiteStorefront && <WhyUseKnotFullWidth />}
					</GridColumn>
					{isLiteStorefront && (
						<GridColumn className={Styles.liteWidget} lg="4">
							<LiteWidget algorithmType="SIMILAR" />
						</GridColumn>
					)}
				</GridRow>
			</PageContainer>
		</RecommendedContextProvider>
	);
};

export default Storefront;
